/**
 * Composant d'affichage des lots techniques avec leurs zones
 * Affichage en mode lecture avec blocs depliables par lot technique
 * - Lot 1xx : affiche les regions
 * - Lot 2xx : affiche les departements
 * FRONT-09: Support du mode verrouille pour les lots AC titulaires uniquement
 */
import React, { useState, useMemo } from 'react';
import './LotsViewByLotTechnique.css';

interface LotDepartement {
  lot_technique_id: number;
  lot_technique_code?: string;
  lot_technique_nom?: string;
  departement_id: number;
  departement_nom?: string;
  departement_code?: string;
  region_id?: number;
  region_nom?: string;
  rang?: number | null; // FRONT-09: Rang dans l'AC
  is_locked?: boolean;  // FRONT-09: Indicateur de verrouillage
}

interface LotTechnique {
  id: number;
  code: string;
  nom: string;
  is_ac?: boolean; // FRONT-09: Lot issu d'un AC (non modifiable)
}

interface LotsViewByLotTechniqueProps {
  lotsDepartements: LotDepartement[];
  lotsTechniques: LotTechnique[];
  emptyMessage?: string;
}

// Determiner si un lot est regional (code 1xx) ou departemental (2xx)
const isLotRegional = (code: string): boolean => {
  const numCode = parseInt(code, 10);
  return numCode >= 100 && numCode < 200;
};

const LotsViewByLotTechnique: React.FC<LotsViewByLotTechniqueProps> = ({
  lotsDepartements,
  lotsTechniques,
  emptyMessage = 'Aucun lot technique selectionne',
}) => {
  // Etat des blocs deplies (par defaut tous repliés)
  const [expandedLots, setExpandedLots] = useState<Set<number>>(() => {
    return new Set();
  });

  // Grouper les departements par lot technique
  const lotsByTechnique = useMemo(() => {
    const grouped = new Map<number, {
      lot: LotTechnique;
      departements: LotDepartement[];
      regions: Map<number, { nom: string; departements: LotDepartement[] }>;
      rang: number | null; // FRONT-09: Rang dans l'AC (premier trouve)
    }>();

    // Initialiser avec les lots techniques
    lotsTechniques.forEach(lot => {
      grouped.set(lot.id, {
        lot,
        departements: [],
        regions: new Map(),
        rang: null,
      });
    });

    // Ajouter les departements a chaque lot
    lotsDepartements.forEach(ld => {
      const lotData = grouped.get(ld.lot_technique_id);
      if (lotData) {
        lotData.departements.push(ld);

        // FRONT-09: Capturer le rang (meme valeur pour tout le lot)
        if (ld.rang !== undefined && ld.rang !== null && lotData.rang === null) {
          lotData.rang = ld.rang;
        }

        // Grouper par region si disponible
        if (ld.region_id && ld.region_nom) {
          if (!lotData.regions.has(ld.region_id)) {
            lotData.regions.set(ld.region_id, {
              nom: ld.region_nom,
              departements: [],
            });
          }
          lotData.regions.get(ld.region_id)?.departements.push(ld);
        }
      }
    });

    return grouped;
  }, [lotsDepartements, lotsTechniques]);

  // Toggle un bloc
  const toggleLot = (lotId: number) => {
    setExpandedLots(prev => {
      const next = new Set(prev);
      if (next.has(lotId)) {
        next.delete(lotId);
      } else {
        next.add(lotId);
      }
      return next;
    });
  };

  if (lotsTechniques.length === 0) {
    return (
      <div className="lots-view-empty">
        <p className="no-data">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="lots-view-by-technique">
      {/* Liste des lots techniques */}
      <div className="lots-blocks">
        {lotsTechniques.map(lot => {
          const lotData = lotsByTechnique.get(lot.id);
          const isExpanded = expandedLots.has(lot.id);
          const isRegional = isLotRegional(lot.code);
          const zoneCount = lotData?.departements.length || 0;
          const rang = lotData?.rang;
          const isLotLocked = lot.is_ac === true; // FRONT-09: Lot AC = verrouille

          return (
            <div key={lot.id} className={`lot-block ${isExpanded ? 'expanded' : 'collapsed'} ${isLotLocked ? 'lot-block--locked' : ''}`}>
              {/* Header du bloc */}
              <div
                className="lot-block-header"
                onClick={() => toggleLot(lot.id)}
              >
                <span className="toggle-icon">{isExpanded ? '[-]' : '[+]'}</span>
                <span className="lot-info">
                  <span className="lot-code">{lot.code}</span>
                  <span className="lot-nom">{lot.nom}</span>
                  {lot.is_ac && <span className="ac-indicator">[AC]</span>}
                  {/* FRONT-09: Afficher le rang si disponible */}
                  {rang !== null && rang !== undefined && (
                    <span className="rang-badge" title={`Rang ${rang} dans l'Accord Cadre`}>
                      Rang {rang}
                    </span>
                  )}
                </span>
                <span className="zone-type">
                  {isRegional ? 'Regional' : 'Departemental'}
                </span>
                <span className="zone-count">
                  {zoneCount} zone{zoneCount > 1 ? 's' : ''}
                </span>
              </div>

              {/* Contenu du bloc (zones) */}
              {isExpanded && (
                <div className="lot-block-content">
                  {zoneCount === 0 ? (
                    <p className="no-zones">Aucune zone selectionnee pour ce lot</p>
                  ) : isRegional ? (
                    // Affichage par regions pour lots 1xx
                    <div className="regions-list">
                      {lotData && Array.from(lotData.regions.entries())
                        .sort((a, b) => a[1].nom.localeCompare(b[1].nom))
                        .map(([regionId, regionData]) => (
                          <span key={regionId} className="region-tag">
                            {regionData.nom}
                          </span>
                        ))}
                      {/* Si pas de regions groupees, afficher les departements */}
                      {lotData && lotData.regions.size === 0 && lotData.departements.map(dept => (
                        <span key={dept.departement_id} className="dept-tag">
                          {dept.departement_code || ''} - {dept.departement_nom}
                        </span>
                      ))}
                    </div>
                  ) : (
                    // Affichage des departements pour lots 2xx
                    <div className="departements-list">
                      {lotData?.departements
                        .sort((a, b) => (a.departement_code || '').localeCompare(b.departement_code || ''))
                        .map(dept => (
                          <span key={dept.departement_id} className="dept-tag">
                            {dept.departement_code} - {dept.departement_nom}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LotsViewByLotTechnique;
